import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Button — Apollo-like :
 *  - default = noir plein (CTA primaire)
 *  - gold    = or (CTA brand fort, ex: valider, soumettre)
 *  - outline = bordure ink-6, hover gris très clair
 *  - ghost   = aucun fond, hover gris
 *  - secondary = gris clair plein
 *  - destructive = rouge
 *  - link    = lien souligné
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-[13px] font-medium ring-offset-background transition-[background-color,border-color,color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-ink-1 shadow-xs",
        gold: "bg-primary text-primary-foreground hover:bg-ink-1 shadow-xs",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-xs",
        outline: "border border-ink-6 bg-background text-foreground hover:bg-ink-8 hover:border-ink-5",
        secondary: "bg-secondary text-secondary-foreground hover:bg-ink-8 border border-transparent",
        ghost: "text-ink-2 hover:bg-ink-8 hover:text-foreground",
        link: "text-foreground underline-offset-4 hover:underline hover:text-gold-dark",
      },
      size: {
        default: "h-9 px-3.5 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-5",
        icon: "h-9 w-9",
        xs: "h-7 rounded px-2 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
